import { getMediaColor } from "../../config/colors";
import type { CampaignReportResponse } from "../../types";
import { formatReportDateLabel } from "../../utils/dateFormatting";

export type RankedMetric = "impressions" | "ctr" | "sales" | "conversions";
export type AdTypeKey = "CTV" | "OLV" | "Display" | "Other";

type LimitMode = "top5" | "all";

const metricLabels: Record<RankedMetric, string> = {
  impressions: "Impressions",
  ctr: "CTR",
  sales: "Sales",
  conversions: "Conversions",
};

export const adTypeColors: Record<AdTypeKey, string> = {
  CTV: "#fb923c",
  OLV: "#8b5cf6",
  Display: "#3b82f6",
  Other: "#94a3b8",
};

export const formatImpressions = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return value.toLocaleString();
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export const parseNumber = (
  value: number | string | null | undefined,
): number => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "");
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const formatCurrencyValue = (value: number | string): string =>
  currencyFormatter.format(parseNumber(value));

export const formatRoasValue = (value: number): string =>
  Number.isFinite(value) && value > 0 ? `${value.toFixed(2)}x` : "—";

const WMC_IDENTIFIERS = ["walmart", "wmc", "wmt", "walmart media collective"];

const matchesWmcIdentifier = (value: string | undefined | null): boolean => {
  if (!value) {
    return false;
  }
  const normalized = value.toLowerCase();
  return WMC_IDENTIFIERS.some((identifier) => normalized.includes(identifier));
};

const detectAdType = (
  adGroupName: string | undefined,
  mediaType?: string | null,
  deviceType?: string | null,
): AdTypeKey => {
  const normalizedName = adGroupName?.toLowerCase() ?? "";
  const normalizedMedia = mediaType?.toLowerCase() ?? "";
  const normalizedDevice = deviceType?.toLowerCase() ?? "";
  if (
    /-\s*CTV\s*$/i.test(adGroupName ?? "") ||
    normalizedName.includes("ctv") ||
    normalizedDevice.includes("connected")
  ) {
    return "CTV";
  }
  if (
    /-\s*(OLV|Video)\s*$/i.test(adGroupName ?? "") ||
    normalizedName.includes("olv") ||
    normalizedName.includes("video") ||
    normalizedMedia.includes("video") ||
    normalizedDevice.includes("video")
  ) {
    return "OLV";
  }
  if (
    /-\s*Display\s*$/i.test(adGroupName ?? "") ||
    normalizedName.includes("display") ||
    normalizedMedia.includes("display")
  ) {
    return "Display";
  }
  return "Other";
};

const extractAudience = (adGroup: string): string =>
  adGroup.replace(/\s*-\s*(Display|OLV|CTV)\s*$/i, "").trim();

const sortByMetric = <T extends Record<RankedMetric, number>>(
  items: T[],
  metric: RankedMetric,
): T[] => [...items].sort((a, b) => b[metric] - a[metric]);

const limitRows = <T>(items: T[], limit: LimitMode): T[] =>
  limit === "top5" ? items.slice(0, 5) : items;

export function buildOffsiteLayoutViewModel({
  audienceLimit,
  audienceMetric,
  creativeLimit,
  creativeMetric,
  data,
  minConversions,
  reportRetailer,
}: {
  audienceLimit: LimitMode;
  audienceMetric: RankedMetric;
  creativeLimit: LimitMode;
  creativeMetric: RankedMetric;
  data: CampaignReportResponse;
  minConversions: number;
  reportRetailer?: string;
}) {
  const {
    summary,
    campaigns,
    daily_metrics,
    ad_groups,
    device_types,
    media_types,
    creatives,
    retailers,
  } = data;

  const totalImpressions = summary.total_impressions;
  const totalConversions = summary.total_units;
  const totalSalesAmount = parseNumber(summary.total_sales);
  const totalSpendAmount = parseNumber(summary.total_spend);
  const averageRoasNumber = Number.parseFloat(
    typeof summary.average_roas === "string"
      ? summary.average_roas
      : String(summary.average_roas ?? 0),
  );
  const normalizedAverageRoas = Number.isFinite(averageRoasNumber)
    ? averageRoasNumber
    : 0;
  const averageRoasDisplay =
    normalizedAverageRoas > 0 ? `${normalizedAverageRoas.toFixed(2)}x` : "—";

  const retailerSummaries = retailers ?? [];
  const retailerNames = retailerSummaries.map((retailer) => retailer.retailer);
  const campaignsList = campaigns ?? [];
  const adGroupList = ad_groups ?? [];

  const isWmcReport =
    matchesWmcIdentifier(reportRetailer) ||
    (retailerSummaries.length === 0 &&
      campaignsList.some((campaign) =>
        matchesWmcIdentifier(campaign.product ?? campaign.campaign_name),
      )) ||
    (retailerSummaries.length === 1 &&
      matchesWmcIdentifier(retailerSummaries[0].retailer));

  const retailerTitleText = (() => {
    if (isWmcReport) {
      return "Walmart Media Collective";
    }
    if (retailerNames.length === 0) {
      const firstCampaign = campaignsList[0];
      return (
        firstCampaign?.product ??
        firstCampaign?.campaign_name ??
        "Offsite Network"
      );
    }
    if (retailerNames.length <= 3) {
      return retailerNames.join(" • ");
    }
    const displayed = retailerNames.slice(0, 3).join(" • ");
    return `${displayed} +${retailerNames.length - 3} more`;
  })();

  const deviceTypes = device_types ?? [];
  const totalDeviceImpressions = deviceTypes.reduce(
    (sum, device) => sum + device.total_impressions,
    0,
  );
  const deviceImpressionDenominator =
    totalDeviceImpressions > 0 ? totalDeviceImpressions : totalImpressions;
  const computeDeviceShare = (impressions: number) =>
    deviceImpressionDenominator > 0
      ? (impressions / deviceImpressionDenominator) * 100
      : 0;
  const deviceColorHex: Record<string, string> = {
    ConnectedTV: "#fbbf24",
    Mobile: "#34d399",
    PC: "#60a5fa",
    Tablet: "#a78bfa",
    Display: "#38bdf8",
    "Online Video": "#f472b6",
    Unknown: "#9ca3af",
  };
  const sortedDeviceTypes = [...deviceTypes].sort(
    (a, b) => b.total_impressions - a.total_impressions,
  );
  const deviceChartData = sortedDeviceTypes.map((device) => ({
    brand_name: device.device_type,
    units: device.total_impressions,
    percentage: computeDeviceShare(device.total_impressions),
  }));
  const deviceChartColors = sortedDeviceTypes.map(
    (device) => deviceColorHex[device.device_type] ?? deviceColorHex.Unknown,
  );
  const deviceAwareness = sortedDeviceTypes.map((device) => ({
    deviceType: device.device_type,
    impressions: device.total_impressions,
    impressionShare: computeDeviceShare(device.total_impressions),
  }));

  const campaignCountsByMedia = campaignsList.reduce<Record<string, number>>(
    (acc, campaign) => {
      const adFormat = campaign.ad_format ?? "";
      const normalized = adFormat.toLowerCase();
      const mediaLabel = normalized.includes("video")
        ? "Video"
        : normalized.includes("display")
          ? "Display"
          : "Other";
      acc[mediaLabel] = (acc[mediaLabel] ?? 0) + 1;
      return acc;
    },
    {},
  );

  const adTypeBuckets = adGroupList.reduce<
    Record<AdTypeKey, { conversions: number; sales: number; spend: number }>
  >(
    (acc, adGroup) => {
      const adType = detectAdType(
        adGroup.ad_group,
        adGroup.media_type,
        adGroup.device_type,
      );
      acc[adType].conversions += parseNumber(adGroup.total_units);
      acc[adType].sales += parseNumber(adGroup.total_sales);
      acc[adType].spend += parseNumber(adGroup.total_spend);
      return acc;
    },
    {
      CTV: { conversions: 0, sales: 0, spend: 0 },
      OLV: { conversions: 0, sales: 0, spend: 0 },
      Display: { conversions: 0, sales: 0, spend: 0 },
      Other: { conversions: 0, sales: 0, spend: 0 },
    },
  );
  const adTypePerformance = (["CTV", "OLV", "Display", "Other"] as const)
    .map((type) => {
      const metrics = adTypeBuckets[type];
      const hasSignal =
        metrics.conversions > 0 || metrics.sales > 0 || metrics.spend > 0;
      if (!hasSignal) {
        return null;
      }
      return {
        type,
        conversions: metrics.conversions,
        sales: metrics.sales,
        spend: metrics.spend,
        roas: metrics.spend > 0 ? metrics.sales / metrics.spend : 0,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const mediaAwareness = media_types
    ? media_types.map((media) => {
        const impressions = media.total_impressions;
        return {
          mediaType: media.media_type,
          impressions,
          impressionShare:
            totalImpressions > 0 ? (impressions / totalImpressions) * 100 : 0,
          color: getMediaColor(media.media_type),
        };
      })
    : [];

  const creativeSummaries = creatives ?? [];
  const creativeRows = creativeSummaries
    .map((creative) => ({
      creative: creative.creative_name,
      impressions: parseNumber(creative.total_impressions),
      clicks: parseNumber(creative.total_clicks),
      ctr: parseFloat(String(creative.ctr)),
      sales: parseNumber(creative.total_sales),
      conversions: parseNumber(creative.total_units),
    }))
    .filter((creative) => {
      if (!isWmcReport || creativeMetric !== "conversions") {
        return true;
      }
      return creative.conversions >= minConversions;
    });
  const creativeData = limitRows(
    sortByMetric(creativeRows, creativeMetric),
    creativeLimit,
  );

  const audienceRows = Object.values(
    adGroupList.reduce<
      Record<
        string,
        {
          audience: string;
          impressions: number;
          clicks: number;
          sales: number;
          conversions: number;
        }
      >
    >((acc, adGroup) => {
      const audience = extractAudience(adGroup.ad_group);
      if (!acc[audience]) {
        acc[audience] = {
          audience,
          impressions: 0,
          clicks: 0,
          sales: 0,
          conversions: 0,
        };
      }
      acc[audience].impressions += adGroup.total_impressions;
      acc[audience].clicks += adGroup.total_clicks;
      acc[audience].sales += parseNumber(adGroup.total_sales);
      acc[audience].conversions += parseNumber(adGroup.total_units);
      return acc;
    }, {}),
  )
    .map((audience) => ({
      ...audience,
      ctr:
        audience.impressions > 0
          ? (audience.clicks / audience.impressions) * 100
          : 0,
    }))
    .filter((audience) => {
      if (!isWmcReport || audienceMetric !== "conversions") {
        return true;
      }
      return audience.conversions >= minConversions;
    });
  const audiences = limitRows(
    sortByMetric(audienceRows, audienceMetric),
    audienceLimit,
  );

  const sortedAdGroups = [...adGroupList]
    .sort((a, b) => b.total_impressions - a.total_impressions)
    .slice(0, 10);
  const dailyLabels = daily_metrics.map((dailyMetric) =>
    formatReportDateLabel(dailyMetric.report_date),
  );
  const dailyAwarenessDatasets = [
    {
      label: "Impressions (000s)",
      data: daily_metrics.map((dailyMetric) =>
        Math.round(dailyMetric.total_impressions / 1000),
      ),
      backgroundColor: "rgba(59, 130, 246, 0.75)",
    },
  ];

  return {
    adGroupImpressions: sortedAdGroups.map((adGroup) => adGroup.total_impressions),
    adGroupLabels: sortedAdGroups.map((adGroup) => adGroup.ad_group),
    adGroupList,
    adTypePerformance,
    audienceMetricLabel: metricLabels[audienceMetric],
    audiences,
    averageRoasDisplay,
    campaignCountsByMedia,
    creativeData,
    creativeMetricLabel: metricLabels[creativeMetric],
    dailyAwarenessDatasets,
    dailyLabels,
    deviceAwareness,
    deviceChartColors,
    deviceChartData,
    hasAdTypePerformance: adTypePerformance.length > 0,
    hasCreativeSummary: creativeSummaries.length > 0,
    isWmcReport,
    mediaAwareness,
    retailerSummaries,
    retailerTitleText,
    shouldShowRetailerCards: !isWmcReport && retailerSummaries.length > 0,
    summary,
    totalConversions,
    totalImpressions,
    totalSalesAmount,
    totalSpendAmount,
  };
}
